/**
 * resolveCardText.js
 * Centralized, type-aware logic to decide which single text snippet to display on cards/tiles.
 * Prevents double-rendering of essentially the same content (caption vs description).
 *
 * Contract
 * - Input item may contain: type, mediaType, caption, description, itemDescription, name, itemName, imageCaptions
 * - Optional second parameter: index to use for per-image captions (defaults to item.thumbnailIndex or 0)
 * - Output: a single, user-facing string (possibly empty) suitable for compact cards.
 * - Rules:
 *   - Suppress for text-only media types (poster/tweet/text) to avoid duplicate blocks; their content is shown in the placeholder area.
 *   - Products/Services: prefer itemDescription, else description, else caption.
 *   - NFTs: prefer caption, else description, else name/itemName; de-duplicate caption===description.
 *   - Documents (mediaType: 'documents'): prefer caption, else description, else itemDescription; de-duplicate.
 *   - Multi-image posts: prefer description, then imageCaptions[index], then caption.
 *   - Generic media (image/video/audio): prefer caption, else description, else itemDescription; de-duplicate.
 */

/** Normalize strings: trim and collapse internal whitespace. */
function norm(s) {
  return typeof s === "string" ? s.trim().replace(/\s+/g, " ") : "";
}

/**
 * Resolve a single display text for a card.
 * @param {Object} item
 * @param {number} [index] - Optional index for per-image captions (defaults to item.thumbnailIndex or 0)
 * @returns {string}
 */
export function resolveCardText(item, index) {
  const it = item || {};
  const type = it.type || null; // 'product' | 'service' | 'media' | 'nft' | ...
  const m = it.mediaType || null; // 'image' | 'video' | 'audio' | 'documents' | 'poster' | 'tweet' | 'text' | 'embed' | ...

  const caption = norm(it.caption);
  const description = norm(it.description);
  const itemDesc = norm(it.itemDescription);
  const nftName = norm(it.name);
  const itemName = norm(it.itemName);
  
  // Per-image caption support
  const imageCaptions = Array.isArray(it.imageCaptions) ? it.imageCaptions : [];
  const images = Array.isArray(it.images) ? it.images : [];
  const thumbIdx = typeof index === 'number' ? index : (it.thumbnailIndex ?? 0);
  const hasMultipleImages = images.length > 1;
  const imageCaption = thumbIdx >= 0 && thumbIdx < imageCaptions.length 
    ? norm(imageCaptions[thumbIdx]) 
    : "";

  // Text-only types: show content in placeholder; suppress info block text
  if (["poster", "tweet", "text"].includes(m)) return "";

  // Product/Service: description-first
  if (type === "product" || type === "service") {
    return itemDesc || description || caption || "";
  }

  // NFT: prefer caption -> description -> name
  if (type === "nft") {
    if (caption && description && caption === description) return caption;
    return caption || description || nftName || itemName || "";
  }

  // Documents: caption or description or itemDescription
  if (m === "documents") {
    if (caption && description && caption === description) return caption;
    return caption || description || itemDesc || "";
  }

  // Multi-image posts: prefer description, then per-image caption, then caption
  if (hasMultipleImages) {
    return description || imageCaption || caption || itemDesc || "";
  }

  // Generic media (image/video/audio): single image case
  // Prefer description over caption when description exists
  if (caption && description && caption === description) return description;
  return description || caption || itemDesc || "";
}

/**
 * Truncate text to a max length, preserving whole characters and adding an ellipsis.
 * @param {string} text
 * @param {number} maxLen
 */
export function truncateText(text, maxLen = 120) {
  const t = typeof text === "string" ? text : "";
  const limit = Math.max(0, Number.isFinite(maxLen) ? maxLen : 120);
  if (t.length <= limit) return t;
  return t.slice(0, limit).trimEnd() + "…";
}
